import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  DISCLAIMER,
  formatAud,
  helpWarningBullet,
  helpWarningHeadline,
  payRiseBullet,
  payRiseHeadline,
  secondJobBullet,
  secondJobHeadline,
  sourceBullet,
  takeHomeBullet,
  takeHomeHeadline,
  withholdingGapHeadline,
} from "@aucash/rules/take-home";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// take-home — net-pay calculator, surfaced stateless-v1 (PLAN arch decision #1).
// One screen, five modes over the shared net-pay engine; the result renders
// INLINE (no save-by-id, no auth gate, no persistence). Each compute is a
// stateless `calc.takeHome.compute` query — input -> output, no round-trip to a
// stored row. Copy comes verbatim from the rules layer's copy helpers so the
// wording can never drift from the API/SEO surfaces (validation §3: information,
// not advice; no "save $X"/"you should"/product handoff — the rules-layer guard
// already strips any such term from the output we render).

const ASSESSMENT_YEAR = "2025-26";

type Mode =
  | "take-home"
  | "pay-rise"
  | "help-warning"
  | "withholding-gap"
  | "second-job";

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "take-home", label: "Net pay" },
  { value: "pay-rise", label: "Pay rise" },
  { value: "help-warning", label: "HELP bill" },
  { value: "withholding-gap", label: "Refund / bill" },
  { value: "second-job", label: "Second job" },
];

const MODE_COPY: Record<Mode, { eyebrow: string; title: string; blurb: string }> = {
  "take-home": {
    eyebrow: "FY26 · net pay",
    title: "What's your real take-home pay?",
    blurb:
      "See your estimated take-home pay for the year from your gross income — income tax, Medicare levy and any compulsory study loan, calculated from current ATO schedules.",
  },
  "pay-rise": {
    eyebrow: "FY26 · keep-rate",
    title: "What does a pay rise actually add?",
    blurb:
      "A pay rise is taxed at the margin. See how much of a raise you'd keep once income tax, the Medicare levy, the low income offset taper and any marginal study-loan step are applied.",
  },
  "help-warning": {
    eyebrow: "FY26 · study loan",
    title: "Is a HELP lump-sum bill building up?",
    blurb:
      "Study-loan withholding is opt-in. If your employer isn't withholding for it, the whole compulsory repayment can land as a lump sum when you lodge. See the estimated figure.",
  },
  "withholding-gap": {
    eyebrow: "FY26 · reconciliation",
    title: "Refund or bill at tax time?",
    blurb:
      "Compare what's been withheld through the year against your estimated annual liability to see a predicted refund or bill. The ATO finalises the actual assessment.",
  },
  "second-job": {
    eyebrow: "FY26 · multiple jobs",
    title: "Claiming the tax-free threshold twice?",
    blurb:
      "Claim the tax-free threshold on only one job. Claiming it on a second job too can leave income under-taxed at the margin — see the estimated shortfall at lodgement.",
  },
};

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.takeHome.compute.query>
>;

export default function TakeHomeIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("take-home");

  // Shared salary-side fields.
  const [grossIncome, setGrossIncome] = useState("");
  const [hasStudyLoan, setHasStudyLoan] = useState(false);

  // pay-rise
  const [riseAmount, setRiseAmount] = useState("");

  // help-warning
  const [employerWithholdsStsl, setEmployerWithholdsStsl] = useState(false);

  // withholding-gap
  const [taxWithheld, setTaxWithheld] = useState("");
  const [stslWithheld, setStslWithheld] = useState("");

  // second-job (its own income fields)
  const [primaryJobIncome, setPrimaryJobIncome] = useState("");
  const [secondJobIncome, setSecondJobIncome] = useState("");
  const [claimsThresholdOnSecondJob, setClaimsThresholdOnSecondJob] =
    useState(true);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.takeHome.compute.query>[0]["input"];
  } | null => {
    if (mode === "second-job") {
      return {
        input: {
          mode: "second-job",
          primaryJobIncome: num(primaryJobIncome),
          secondJobIncome: num(secondJobIncome),
          claimsThresholdOnSecondJob,
          assessmentYear: ASSESSMENT_YEAR,
        },
      };
    }
    const base = {
      grossIncome: num(grossIncome),
      hasStudyLoan,
      assessmentYear: ASSESSMENT_YEAR,
    };
    switch (mode) {
      case "take-home":
        return { input: { mode: "take-home", ...base } };
      case "pay-rise":
        return { input: { mode: "pay-rise", ...base, riseAmount: num(riseAmount) } };
      case "help-warning":
        return {
          input: {
            mode: "help-warning",
            ...base,
            hasStudyLoan: true,
            employerWithholdsStsl,
          },
        };
      case "withholding-gap":
        return {
          input: {
            mode: "withholding-gap",
            ...base,
            taxWithheld: num(taxWithheld),
            stslWithheld: num(stslWithheld),
          },
        };
    }
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (mode === "second-job") {
      if (primaryJobIncome.trim() === "")
        next.primary = "Enter the income from your main job.";
      if (secondJobIncome.trim() === "")
        next.second = "Enter the income from your second job.";
      return next;
    }
    if (grossIncome.trim() === "")
      next.gross = "Enter your gross income for the year.";
    if (mode === "pay-rise" && riseAmount.trim() === "")
      next.rise = "Enter the pay rise you want to model.";
    if (mode === "withholding-gap" && taxWithheld.trim() === "")
      next.withheld = "Enter the tax withheld through the year.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.takeHome.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = MODE_COPY[mode];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Take-home pay" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="Which question are you answering?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          {mode === "second-job" ? (
            <>
              <Field
                label="Income from your main job"
                help="The higher-paying job — the one you claim the tax-free threshold on."
                error={errors.primary}
              >
                <MoneyInput
                  value={primaryJobIncome}
                  onChange={setPrimaryJobIncome}
                  testID="input-primary-income"
                  accessibilityLabel="Income from your main job"
                />
              </Field>
              <Field
                label="Income from your second job"
                error={errors.second}
              >
                <MoneyInput
                  value={secondJobIncome}
                  onChange={setSecondJobIncome}
                  testID="input-second-income"
                  accessibilityLabel="Income from your second job"
                />
              </Field>
              <Field label="Are you claiming the tax-free threshold on the second job too?">
                <Checkbox
                  checked={claimsThresholdOnSecondJob}
                  onChange={setClaimsThresholdOnSecondJob}
                  label="Yes — the tax-free threshold is claimed on the second job as well."
                />
              </Field>
            </>
          ) : (
            <>
              <Field
                label="Your gross income for FY26"
                help="Your assessable income minus deductions — before tax is taken out."
                error={errors.gross}
              >
                <MoneyInput
                  value={grossIncome}
                  onChange={setGrossIncome}
                  testID="input-gross-income"
                  accessibilityLabel="Your gross income for FY26"
                />
              </Field>

              {mode === "pay-rise" ? (
                <Field
                  label="The pay rise you're modelling"
                  help="The gross annual increase — we'll split it into what's withheld and what you keep."
                  error={errors.rise}
                >
                  <MoneyInput
                    value={riseAmount}
                    onChange={setRiseAmount}
                    testID="input-rise-amount"
                    accessibilityLabel="The pay rise you're modelling"
                  />
                </Field>
              ) : null}

              {mode === "withholding-gap" ? (
                <>
                  <Field
                    label="Tax withheld through the year"
                    help="The PAYG tax + Medicare withheld from your pay (from your payslips or income statement), excluding study-loan amounts."
                    error={errors.withheld}
                  >
                    <MoneyInput
                      value={taxWithheld}
                      onChange={setTaxWithheld}
                      testID="input-tax-withheld"
                      accessibilityLabel="Tax withheld through the year"
                    />
                  </Field>
                  <Field label="Study-loan amounts withheld (if any)">
                    <MoneyInput
                      value={stslWithheld}
                      onChange={setStslWithheld}
                      accessibilityLabel="Study loan amounts withheld"
                    />
                  </Field>
                </>
              ) : null}

              {mode === "help-warning" ? (
                <Field label="Is your employer withholding for your study loan?">
                  <Checkbox
                    checked={employerWithholdsStsl}
                    onChange={setEmployerWithholdsStsl}
                    label="Yes — my employer withholds study and training loan amounts from my pay."
                  />
                </Field>
              ) : (
                <Field label="Do you have a HELP / study loan?">
                  <Checkbox
                    checked={hasStudyLoan}
                    onChange={setHasStudyLoan}
                    label="Yes — I have an outstanding HECS-HELP or other study and training loan."
                  />
                </Field>
              )}
            </>
          )}

          <Button
            label={submitting ? "Calculating…" : "See your estimate →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="take-home-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer DISCLAIMER) sits at the TOP, before any
 * number (validation §3.2 placement rule). */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const { headline, bullet } = headlineFor(result);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · FY26</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        {bullet !== null ? <Text style={styles.bodyText}>{bullet}</Text> : null}
      </Card>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(result.ruleVersion, result.sourcesVerifiedAt)}
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Pick the rules-layer copy helper for the result's mode. All wording comes
 * from the rules package — no inline figures, no advice framing. */
function headlineFor(result: ComputeResult): {
  headline: string;
  bullet: string | null;
} {
  const out = result.output;
  switch (result.mode) {
    case "take-home": {
      const o = out as Extract<typeof out, { takeHome: number }>;
      return { headline: takeHomeHeadline(o), bullet: takeHomeBullet(o) };
    }
    case "pay-rise": {
      const o = out as Extract<typeof out, { keptAmount: number }>;
      return { headline: payRiseHeadline(o), bullet: payRiseBullet(o) };
    }
    case "help-warning": {
      const o = out as Extract<typeof out, { silentBill: boolean }>;
      return { headline: helpWarningHeadline(o), bullet: helpWarningBullet(o) };
    }
    case "withholding-gap": {
      const o = out as Extract<typeof out, { outcome: string }>;
      return { headline: withholdingGapHeadline(o), bullet: null };
    }
    case "second-job": {
      const o = out as Extract<typeof out, { predictedShortfall: number }>;
      return { headline: secondJobHeadline(o), bullet: secondJobBullet(o) };
    }
  }
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

// `formatAud` is imported for parity with the other screens' result helpers; the
// headline strings already embed the formatted figures, so it isn't called
// directly here. Referenced to keep the import meaningful without a no-op.
void formatAud;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
